<template>
  <v-container fluid class="custom-line-item-template">
    <!-- Summary Header Cards -->
    <v-row class="mb-4" v-if="receivedLineItem.length > 0">
      <v-col cols="12" sm="4">
        <v-card
          flat
          class="custom-grey-border pa-3 rounded-lg d-flex align-center"
        >
          <v-avatar color="cyan lighten-5" size="42" class="mr-3">
            <v-icon color="cyan darken-2">mdi-counter</v-icon>
          </v-avatar>
          <div>
            <div class="caption grey--text font-weight-600 text-uppercase">
              Total Received Logs
            </div>
            <div class="title font-weight-700 cyan--text text--darken-2">
              {{ totalReceivedEntries }}
            </div>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="4">
        <v-card
          flat
          class="custom-grey-border pa-3 rounded-lg d-flex align-center"
        >
          <v-avatar color="teal lighten-5" size="42" class="mr-3">
            <v-icon color="teal darken-2">mdi-counter</v-icon>
          </v-avatar>
          <div>
            <div class="caption grey--text font-weight-600 text-uppercase">
              Total Qty Received
            </div>
            <div class="title font-weight-700 teal--text text--darken-2">
              {{ totalReceivedQty }}
            </div>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="4">
        <v-card
          flat
          class="custom-grey-border pa-3 rounded-lg d-flex align-center"
        >
          <v-avatar color="blue lighten-5" size="42" class="mr-3">
            <v-icon color="blue darken-2">mdi-check-all</v-icon>
          </v-avatar>
          <div>
            <div class="caption grey--text font-weight-600 text-uppercase">
              Receiving Status
            </div>
            <v-chip
              small
              class="font-weight-600 mt-1"
              color="green lighten-4"
              text-color="green darken-3"
            >
              Items Received
            </v-chip>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Search & Filter Controls -->
    <v-row class="mb-2 align-center" v-if="totalRows > 0 || searchQuery">
      <v-col cols="12" sm="6" md="5">
        <v-text-field
          solo
          flat
          v-model="searchQuery"
          dense
          outlined
          hide-details
          clearable
          color="cyan"
          prepend-inner-icon="mdi-magnify"
          placeholder="Search by Product Name or Barcode..."
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="6" md="7" class="d-flex justify-end">
        <v-btn
          depressed
          color="cyan"
          class="white--text font-weight-600 text-uppercase"
          v-on:click="getReceivedItems"
          :loading="pageLoading"
        >
          <v-icon left>mdi-refresh</v-icon>
          Refresh Received List
        </v-btn>
      </v-col>
    </v-row>

    <v-skeleton-loader
      v-if="pageLoading"
      class="custom-skeleton"
      type="table-tbody"
    ></v-skeleton-loader>
    <div v-else class="received-table-scroll">
      <v-simple-table class="simple-table">
      <template v-slot:default>
        <thead>
          <tr style="background-color: #f3f6f9; color: #24326d">
            <td class="font-size-13 text-uppercase font-weight-500">Product</td>
            <td
              style="width: 150px"
              class="font-size-13 text-uppercase font-weight-500"
            >
              PO Qty
            </td>
            <td
              style="width: 160px"
              class="font-size-13 text-uppercase font-weight-500"
            >
              Received Qty
            </td>
            <td
              style="width: 180px"
              class="font-size-13 text-uppercase font-weight-500"
            >
              Remaining Pending
            </td>
            <td
              style="width: 240px"
              class="font-size-13 text-uppercase font-weight-500"
            >
              Created Time
            </td>
          </tr>
        </thead>
        <tbody v-if="filteredReceivedItems.length > 0">
          <tr
            v-for="(item, index) in filteredReceivedItems"
            :key="item.id || index"
            :class="{
              'custom-border-top': index == 0,
              'orange_lighten-5': index % 2 == 0,
              'cursor-pointer': true,
            }"
            role="button"
            tabindex="0"
            v-on:click="openReceivedOverview(item)"
            v-on:keyup.enter="openReceivedOverview(item)"
          >
            <td class="font-size-14 font-weight-500">
              <div class="detail-line-item py-1">
                <p class="m-0 text-capitalize custom-nowrap-ellipsis">
                  {{ getProductName(item) }}
                </p>
                <div
                  v-if="getProductBarcode(item)"
                  class="d-inline-block"
                  v-on:click.stop
                  v-on:keyup.enter.stop
                >
                  <Barcode
                    small
                    dense
                    :barcode="getProductBarcode(item)"
                    :id="getProductId(item)"
                    route="product.detail"
                  ></Barcode>
                </div>
              </div>
            </td>
            <td class="font-size-14 font-weight-500">
              {{ item.po_qty != null ? item.po_qty : item.quantity }}
            </td>
            <td class="font-size-14 font-weight-500">
              <v-chip
                small
                color="green lighten-4"
                text-color="green darken-3"
                class="font-weight-700"
              >
                <v-icon small left color="green darken-3">mdi-plus</v-icon>
                {{ item.received_qty }}
              </v-chip>
            </td>
            <td class="font-size-14 font-weight-500">
              {{ item.pending_qty != null ? item.pending_qty : 0 }}
            </td>
            <td class="font-size-14 font-weight-500">
              <TableActivity v-if="!lodash.isEmpty(item.added_by)" :data="item">
                <template v-slot:first_letters>
                  {{ getFirstLetters(item.added_by.display_name) }}
                </template>
                <template v-slot:display_name>
                  {{ item.added_by.display_name }}
                </template>
                <template v-slot:date_time>
                  {{ item.created_at }}
                </template>
                <template v-slot:format_date_time>
                  {{ formatDateTime(item.added_at) }}
                </template>
              </TableActivity>
              <em v-else class="text-muted"> no activity </em>
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr>
            <td colspan="5">
              <p
                class="m-0 py-4 row-not-found text-center font-weight-500 font-size-16"
              >
                <img
                  :src="$assetURL('media/error/empty.png')"
                  class="row-not-found-image"
                />
                Uhh... There are no line item received at the moment.
              </p>
            </td>
          </tr>
        </tbody>
      </template>
      </v-simple-table>
    </div>
    <div v-if="totalRows > 0" class="listing-footer-fixed">
      <ListingFooter
        :dataLoading="pageLoading"
        :showingFrom="showingFrom"
        :showingTo="showingTo"
        :totalRows="totalRows"
        :currentPage="currentPage"
        :totalPages="totalPages"
        removePage
      ></ListingFooter>
    </div>

    <Dialog :commonDialog="receivedOverviewDialog" :dialogWidth="900">
      <template v-slot:title>
        <div class="d-flex align-center width-100">
          <v-icon color="cyan darken-2" class="mr-2"
            >mdi-package-variant-closed-check</v-icon
          >
          <span class="font-weight-700 color-custom-blue"
            >Received Line Item Overview</span
          >
          <v-spacer></v-spacer>
          <v-btn
            icon
            aria-label="Close received line item overview"
            v-on:click="closeReceivedOverview"
          >
            <v-icon color="grey darken-1">mdi-close</v-icon>
          </v-btn>
        </div>
      </template>

      <template v-slot:body>
        <div v-if="selectedReceivedItem" class="px-4">
          <!-- Top Info Row: Product Details & Activity Log -->
          <v-row class="mb-2">
            <v-col cols="12" md="7">
              <div
                class="caption text-uppercase font-weight-700 grey--text text--darken-2 mb-1"
              >
                Product Details
              </div>
              <div
                class="subtitle-1 font-weight-700 text-capitalize color-custom-blue"
              >
                {{ getProductName(selectedReceivedItem) }}
              </div>
              <div v-if="getProductBarcode(selectedReceivedItem)" class="mt-2">
                <Barcode
                  small
                  dense
                  :barcode="getProductBarcode(selectedReceivedItem)"
                  :id="getProductId(selectedReceivedItem)"
                  route="product.detail"
                ></Barcode>
              </div>
            </v-col>
            <v-col cols="12" md="5">
              <div
                class="caption text-uppercase font-weight-700 grey--text text--darken-2 mb-1"
              >
                Received Activity Log
              </div>
              <TableActivity
                v-if="!lodash.isEmpty(selectedReceivedItem.added_by)"
                :data="selectedReceivedItem"
              >
                <template v-slot:first_letters>
                  {{
                    getFirstLetters(selectedReceivedItem.added_by.display_name)
                  }}
                </template>
                <template v-slot:display_name>
                  {{ selectedReceivedItem.added_by.display_name }}
                </template>
                <template v-slot:date_time>
                  {{ selectedReceivedItem.created_at }}
                </template>
                <template v-slot:format_date_time>
                  {{ formatDateTime(selectedReceivedItem.added_at) }}
                </template>
              </TableActivity>
              <em v-else class="text-muted">No activity record</em>
            </v-col>
          </v-row>

          <!-- Quantity Summary Card -->
          <v-card
            flat
            class="grey lighten-4 custom-grey-border rounded-lg pa-4 mb-4"
          >
            <v-row align="center">
              <v-col cols="12" sm="4" class="text-center text-sm-left">
                <div
                  class="caption text-uppercase font-weight-700 grey--text text--darken-2 mb-1"
                >
                  PO Quantity
                </div>
                <div class="headline font-weight-700 color-custom-blue">
                  {{
                    selectedReceivedItem.po_qty != null
                      ? selectedReceivedItem.po_qty
                      : selectedReceivedItem.quantity
                  }}
                </div>
              </v-col>
              <v-col cols="12" sm="4" class="text-center text-sm-left">
                <div
                  class="caption text-uppercase font-weight-700 grey--text text--darken-2 mb-1"
                >
                  Received Quantity
                </div>
                <v-chip
                  small
                  color="green lighten-4"
                  text-color="green darken-3"
                  class="font-weight-700 mt-1 px-2"
                >
                  <v-icon small left color="green darken-3"> mdi-plus </v-icon>
                  {{ selectedReceivedItem.received_qty }}
                </v-chip>
              </v-col>
              <v-col cols="12" sm="4" class="text-center text-sm-left">
                <div
                  class="caption text-uppercase font-weight-700 grey--text text--darken-2 mb-1"
                >
                  Remaining Pending
                </div>
                <div class="headline font-weight-700 color-custom-blue">
                  {{
                    selectedReceivedItem.pending_qty != null
                      ? selectedReceivedItem.pending_qty
                      : 0
                  }}
                </div>
              </v-col>
            </v-row>
          </v-card>

          <!-- Remark Section -->
          <div class="mb-4">
            <div
              class="caption text-uppercase font-weight-700 grey--text text--darken-2 mb-1"
            >
              Remark / Notes
            </div>
            <div
              v-if="selectedReceivedItem.remark"
              class="custom-grey-border rounded-lg pa-3 bg-white"
            >
              {{ selectedReceivedItem.remark }}
            </div>
            <em v-else class="text-muted">No remark provided</em>
          </div>

          <!-- Serial Numbers / Barcodes Section -->
          <v-card
            v-if="isSerializedProduct(selectedReceivedItem)"
            flat
            outlined
            class="mb-4 pa-3"
          >
            <div class="d-flex align-center justify-space-between flex-wrap">
              <div class="d-flex align-center">
                <v-avatar color="cyan lighten-5" size="34" class="mr-2">
                  <v-icon small color="cyan darken-2">mdi-barcode-scan</v-icon>
                </v-avatar>
                <div>
                  <div class="font-weight-700 color-custom-blue">
                    Serial Numbers / Barcodes
                  </div>
                  <div class="caption grey--text text--darken-1">
                    {{ getSerialNumbers(selectedReceivedItem).length }} recorded ·
                    click a serial to open its barcode
                  </div>
                </div>
              </div>
              <v-text-field
                v-model="serialSearch"
                class="received-serial-search mt-2 mt-sm-0"
                dense
                outlined
                rounded
                clearable
                hide-details
                color="cyan"
                prepend-inner-icon="mdi-magnify"
                placeholder="Search serial number..."
              ></v-text-field>
            </div>
            <div class="received-serial-card-scroll mt-3">
              <div
                v-if="serialLoading && !getSerialNumbers(selectedReceivedItem).length"
                class="text-center py-8"
              >
                <v-progress-circular
                  indeterminate
                  color="cyan"
                ></v-progress-circular>
              </div>
              <div class="serial-list-grid" v-if="getFilteredSerialNumbers(selectedReceivedItem).length">
                <div
                  v-for="(serial, index) in getFilteredSerialNumbers(selectedReceivedItem)"
                  :key="serial.id || index"
                  class="received-serial-card pa-3"
                >
                  <div class="d-flex align-center justify-space-between">
                    <span
                      class="received-serial-text font-weight-700 text-truncate mr-1"
                      :title="getSerialValue(serial)"
                    >
                      {{ getSerialValue(serial) }}
                    </span>
                    <div class="d-flex align-center">
                      <v-btn
                        icon
                        x-small
                        title="Copy Serial Number"
                        @click.stop="copySerialNumber(getSerialValue(serial))"
                      >
                        <v-icon size="14" color="#166534">mdi-content-copy</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        x-small
                        title="View Barcode"
                        @click.stop="openSerialBarcode(serial)"
                      >
                        <v-icon size="20" color="#0891b2">mdi-qrcode-scan</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else-if="!serialLoading" class="text-center py-8 grey--text">
                No serial numbers found.
              </div>
            </div>
            <div v-if="serialPage < serialTotalPages" class="text-center mt-3">
              <v-btn
                small
                outlined
                color="cyan darken-2"
                :loading="serialLoading"
                @click="loadMoreReceivedSerials"
              >
                More Results
              </v-btn>
            </div>
          </v-card>

          <!-- Attachments Section -->
          <div>
            <div
              class="caption text-uppercase font-weight-700 grey--text text--darken-2 mb-1"
            >
              Attachments
            </div>
            <FileTemplate
              v-if="
                selectedReceivedItem.attachments &&
                selectedReceivedItem.attachments.length
              "
              isMinDisplay
              :allowDelete="false"
              :attachments="selectedReceivedItem.attachments"
            ></FileTemplate>
            <em v-else class="text-muted">No attachments uploaded</em>
          </div>
        </div>
      </template>

      <template v-slot:action>
        <v-btn
          depressed
          class="custom-grey-border custom-bold-button px-6"
          v-on:click="closeReceivedOverview"
        >
          Close
        </v-btn>
      </template>
    </Dialog>

    <Dialog :commonDialog="serialBarcodeDialog" :dialogWidth="360">
      <template v-slot:title>
        <div class="d-flex align-center justify-space-between width-100">
          <span class="font-weight-700">Barcode</span>
          <v-btn icon small @click="serialBarcodeDialog = false">
            <v-icon color="#000">mdi-close</v-icon>
          </v-btn>
        </div>
      </template>

      <!-- <template v-slot:body>
        <div v-if="selectedSerialBarcode" class="px-2">
          <div class="barcode-preview-box pa-6 d-flex flex-column align-center justify-start">
            <img
              v-if="selectedSerialBarcode.barcode_image"
              :src="selectedSerialBarcode.barcode_image"
              class="serial-barcode-image"
              alt="Barcode"
            />
            <v-icon v-else size="96" color="#334155">mdi-qrcode</v-icon>

            <div class="serial-pill-badge mt-4">
              {{ getSerialValue(selectedSerialBarcode) }}
            </div>
          </div>
        </div>
      </template> -->
      <template v-slot:body>
        <div v-if="selectedSerialBarcode" class="px-2">
          <div class="barcode-preview-box d-flex flex-column align-center justify-center">
            <img
              v-if="selectedSerialBarcode.barcode_image"
              :src="selectedSerialBarcode.barcode_image"
              class="serial-barcode-image"
              alt="Barcode"
            />

            <v-icon
              v-else
              size="96"
              color="#334155"
            >
              mdi-qrcode
            </v-icon>

            <div class="serial-pill-badge mt-4">
              {{ getSerialValue(selectedSerialBarcode) }}
            </div>
          </div>
        </div>
      </template>

      <template v-slot:action>
        <v-btn
          depressed
          class="mx-2 custom-grey-border custom-bold-button"
          @click="serialBarcodeDialog = false"
        >
          Close
        </v-btn>
      </template>
    </Dialog>
  </v-container>
</template>

<script>
import { QUERY } from "@/core/services/store/request.module";
import TableActivity from "@/view/pages/partials/Table-Activity.vue";
import ListingFooter from "@/view/pages/partials/Listing-Footer.vue";
import Barcode from "@/view/pages/partials/Barcode.vue";
import FileTemplate from "@/view/pages/partials/FileTemplate.vue";
import Dialog from "@/view/pages/partials/Dialog.vue";
import CommonMixin from "@/core/plugins/common-mixin";
import { PaginationEventBus } from "@/core/lib/pagination.lib";
import { SuccessEventBus } from "@/core/lib/message.lib";

export default {
  name: "received-line-item",
  mixins: [CommonMixin],
  props: {
    purchaseOrder: {
      required: true,
      type: Number,
      default: 0,
    },
  },
  components: {
    TableActivity,
    ListingFooter,
    Barcode,
    FileTemplate,
    Dialog,
  },
  data: () => {
    return {
      formValid: true,
      productLoading: false,
      pageLoading: true,
      searchQuery: "",
      receivedLineItem: new Array(),
      currentPage: 1,
      totalRows: 0,
      totalPages: 1,
      perPage: 15,
      rowsOffset: 0,
      totalReceivedQuantity: 0,
      searchTimer: null,
      receivedOverviewDialog: false,
      selectedReceivedItem: null,
      serialSearch: "",
      receivedSerials: [],
      serialLoading: false,
      serialPage: 1,
      serialTotalPages: 1,
      serialTotalRows: 0,
      serialPerPage: 50,
      serialSearchTimer: null,
      serialRequestToken: 0,
      serialBarcodeDialog: false,
      selectedSerialBarcode: null,
    };
  },
  watch: {
    receivedOverviewDialog(isOpen) {
      if (!isOpen) {
        this.selectedReceivedItem = null;
        this.serialSearch = "";
        this.receivedSerials = [];
        this.serialTotalRows = 0;
        this.serialPage = 1;
        this.serialTotalPages = 1;
      }
    },
    serialSearch(value) {
      clearTimeout(this.serialSearchTimer);
      if (
        !this.receivedOverviewDialog ||
        !this.isSerializedProduct(this.selectedReceivedItem)
      ) {
        return;
      }

      this.serialSearchTimer = setTimeout(() => {
        this.loadReceivedSerials(true, value || "");
      }, 300);
    },
    searchQuery() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.currentPage = 1;
        this.getReceivedItems();
      }, 400);
    },
  },
  computed: {
    totalReceivedEntries() {
      return this.totalRows;
    },
    totalReceivedQty() {
      return this.totalReceivedQuantity;
    },
    filteredReceivedItems() {
      return this.receivedLineItem;
    },
    showingFrom() {
      return this.receivedLineItem.length ? this.rowsOffset + 1 : 0;
    },
    showingTo() {
      return this.rowsOffset + this.receivedLineItem.length;
    },
  },
  methods: {
    openReceivedOverview(item) {
      this.selectedReceivedItem = item;
      this.serialSearch = "";
      this.receivedSerials = [];
      this.serialPage = 1;
      this.serialTotalPages = 1;
      this.serialTotalRows = 0;
      this.receivedOverviewDialog = true;
      if (this.isSerializedProduct(item)) {
        this.loadReceivedSerials(true);
      }
    },
    closeReceivedOverview() {
      this.receivedOverviewDialog = false;
      this.selectedReceivedItem = null;
    },
    openSerialBarcode(serial) {
      this.selectedSerialBarcode = serial;
      this.serialBarcodeDialog = true;
    },
    copySerialNumber(value) {
      if (!value || !navigator.clipboard) return;

      navigator.clipboard.writeText(value).then(() => {
        SuccessEventBus.$emit("update:success", `${value} successfully copied.`);
      });
    },
    handlePagination(page) {
      this.currentPage = page;
      this.getReceivedItems();
    },
    getProductName(item) {
      if (!item) return "-";
      const p = item.product_data || item.product;
      if (p && typeof p === "object" && p.name) return p.name;
      return "-";
    },
    getProductBarcode(item) {
      if (!item) return "";
      const p = item.product_data || item.product;
      if (p && typeof p === "object" && p.barcode) return p.barcode;
      return "";
    },
    getProductId(item) {
      if (!item) return 0;
      const p = item.product_data || item.product;
      if (p && typeof p === "object" && p.id) {
        return Number(p.id);
      }
      return Number(item.product_id) || 0;
    },
    getSerialNumbers(item) {
      if (!this.isSerializedProduct(item)) {
        return [];
      }

      const serials = item === this.selectedReceivedItem
        ? this.receivedSerials
        : item.serial_numbers;

      return Array.isArray(serials)
        ? serials.filter((serial) => this.getSerialValue(serial))
        : [];
    },
    getFilteredSerialNumbers(item) {
      const search = String(this.serialSearch || "").toLowerCase().trim();

      return this.getSerialNumbers(item).filter((serial) => {
        return !search || this.getSerialValue(serial).toLowerCase().includes(search);
      });
    },
    isSerializedProduct(item) {
      const product = item && (item.product_data || item.product);
      return Boolean(
        product &&
          Number(product.isSerialized || product.is_serialized || product.trackSerial) ===
            1
      );
    },
    getSerialValue(serial) {
      if (!serial) return "";
      if (typeof serial === "string") return serial.trim();

      return String(serial.barcode || serial.text || "").trim();
    },
    getProductType(item) {
      const product = item && (item.product_data || item.product);
      return (
        (product && product.product_type) ||
        (item && item.product_type) ||
        null
      );
    },
    loadReceivedSerials(reset = false, searchTerm = this.serialSearch) {
      const productId = this.getProductId(this.selectedReceivedItem);
      const receiveId = Number(this.selectedReceivedItem && this.selectedReceivedItem.id);
      if (!productId || !receiveId || (!reset && this.serialLoading)) return;

      if (reset) {
        this.serialRequestToken += 1;
        this.receivedSerials = [];
        this.serialPage = 1;
        this.serialTotalPages = 1;
        this.serialTotalRows = 0;
      }

      const requestToken = this.serialRequestToken;
      const page = this.serialPage;
      this.serialLoading = true;
      this.$store
        .dispatch(QUERY, {
          url: `purchase-order/${this.purchaseOrder}/received-serials`,
          data: {
            product_id: productId,
            product_type: this.getProductType(this.selectedReceivedItem),
            po_receive_id: receiveId,
            current_page: page,
            per_page: this.serialPerPage,
            search: searchTerm || null,
          },
        })
        .then(({ data }) => {
          if (requestToken !== this.serialRequestToken) return;

          const incoming = Array.isArray(data.rows) ? data.rows : [];
          const existingIds = new Set(this.receivedSerials.map((serial) => serial.id));
          this.receivedSerials = this.receivedSerials.concat(
            incoming.filter((serial) => !existingIds.has(serial.id))
          );
          this.serialPage = Number(data.current_page) || page;
          this.serialTotalPages = Number(data.total_pages) || 1;
          this.serialTotalRows = Number(data.total_rows) || 0;
        })
        .catch(() => {
          if (requestToken === this.serialRequestToken && reset) {
            this.receivedSerials = [];
            this.serialTotalRows = 0;
          }
        })
        .finally(() => {
          if (requestToken === this.serialRequestToken) {
            this.serialLoading = false;
          }
        });
    },
    loadMoreReceivedSerials() {
      if (this.serialLoading || this.serialPage >= this.serialTotalPages) return;
      this.serialPage += 1;
      this.loadReceivedSerials();
    },
    getReceivedItems() {
      const _this = this;
      _this.pageLoading = true;
      _this.$store
        .dispatch(QUERY, {
          url: "purchase-order/" + _this.purchaseOrder + "/received",
          data: {
            current_page: _this.currentPage,
            per_page: _this.perPage,
            search: _this.searchQuery || null,
          },
        })
        .then(({ data }) => {
          _this.receivedLineItem = data.rows || [];
          _this.currentPage = data.current_page || 1;
          _this.perPage = data.per_page || 15;
          _this.rowsOffset = data.offset || 0;
          _this.totalRows = data.total_rows || 0;
          _this.totalPages = Math.max(1, data.total_pages || 1);
          _this.totalReceivedQuantity = data.total_received_quantity || 0;
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.pageLoading = false;
        });
    },
  },
  mounted() {
    this.getReceivedItems();
    PaginationEventBus.$on("update:pagination", this.handlePagination);
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
    clearTimeout(this.serialSearchTimer);
    PaginationEventBus.$off("update:pagination", this.handlePagination);
  },
};
</script>

<style scoped>
.custom-line-item-template {
  height: 100%;
  overflow: hidden;
}

.received-table-scroll {
  max-height: calc(100vh - 340px);
  overflow-y: auto;
}

.received-table-scroll ::v-deep .v-data-table__wrapper {
  overflow: visible !important;
}

.received-table-scroll ::v-deep table {
  border-collapse: separate;
}

.received-table-scroll ::v-deep thead {
  position: sticky;
  top: 0;
  z-index: 2;
}

.received-table-scroll ::v-deep thead td {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #f3f6f9;
}

/* .listing-footer-fixed {
  position: fixed;
  bottom: 0;
  left: 500px;
  right: 0;
  background: #fff;
  z-index: 100;
} */

.serial-list-scroll {
  max-height: 430px;
  overflow-y: auto;
}

.received-serial-card-scroll {
  max-height: 430px;
  overflow-y: auto;
  overflow-x: hidden;
}

.serial-list-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
}

@media (max-width: 700px) {
  .serial-list-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 420px) {
  .serial-list-grid {
    grid-template-columns: 1fr;
  }
}

.received-serial-search {
  max-width: 260px;
}

.received-serial-card {
  min-height: 58px;
  background: #f4fbf7;
  border: 1.5px solid #d1fae5;
  border-radius: 10px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.received-serial-card:hover {
  border-color: #059669;
  box-shadow: 0 2px 8px rgba(5, 150, 105, 0.12);
}

.received-serial-text {
  color: #166534;
}

.barcode-preview-box {
  width: 100%;
  height: 320px;
  padding: 20px;
  border: 1px dashed #cbd5e1;
  border-radius: 16px;
  box-sizing: border-box;
}

.serial-barcode-image {
  width: 100%;
  max-width: 100%;
  height: 220px;
  object-fit: contain;
  display: block;
}

.serial-pill-badge {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1d4ed8;
  font-weight: 700;
  font-size: 13px;
  border-radius: 20px;
  padding: 4px 22px;
}
</style>
